
import { ConnectionListResult, PaginatedQueryWrapper } from '@tableau/data-catalog-shared';
import { SiteUrls } from '@tableau/shared-urls';
import { CatalogUrlParamKeys } from '@tableau/shared-urls/lib/src/SiteUrls';
import * as React from 'react';
import { Messages } from '../../../gen-src/Localize';
import {
  ColumnFieldsQuery,
  ColumnFieldsQuery_columns_referencedByFieldsConnection_nodes,
  ColumnFieldsQuery_columns_referencedByFieldsConnection_nodes_datasource,
  ColumnFieldsQueryVariables,
} from '../../../gen-src/queries/ColumnFieldsQuery';
import { ColumnFieldsQueryGql } from '../../queries/ColumnFieldsQueryGql';
import { LinksDisplayGrid, LinksDisplayGridRowData } from '../sharedDisplayGrid/LinksDisplayGrid';

interface ColumnFieldsGridRowData extends LinksDisplayGridRowData {
  readonly publishedDatasourceVizportalUrlId: string;
  readonly embeddedDatasourceNodeId: string;
  readonly datasourceName: string;
  readonly workbookVizportalUrlId: string;
}

type QueryVariables = ColumnFieldsQueryVariables;
type QueryResults = ColumnFieldsQuery;
type QueryResultListNode = ColumnFieldsQuery_columns_referencedByFieldsConnection_nodes;
type GridRowData = ColumnFieldsGridRowData;

function selectConnectionListResult(resultData: ColumnFieldsQuery): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.columns && resultData.columns[0] && resultData.columns[0].referencedByFieldsConnection;
}

function getWorkbookId(
  datasource: ColumnFieldsQuery_columns_referencedByFieldsConnection_nodes_datasource) {
  return datasource.__typename === 'EmbeddedDatasource' && datasource.workbook && datasource.workbook.vizportalUrlId;
}

function mapResultNode(node: QueryResultListNode): GridRowData {
  const datasource = node.datasource;
  return {
    id: node.id,
    name: node.name,
    publishedDatasourceVizportalUrlId: datasource && datasource.__typename === 'PublishedDatasource' && datasource.vizportalUrlId || '',
    embeddedDatasourceNodeId: datasource && datasource.__typename === 'EmbeddedDatasource' && datasource.id || '',
    datasourceName: node.datasource && node.datasource.name || '',
    workbookVizportalUrlId: node.datasource && getWorkbookId(node.datasource) || ''
  };
}

function urlForLink(urls: SiteUrls, rowData: ColumnFieldsGridRowData) {
  return rowData.workbookVizportalUrlId ?
    urls.workbookLineageFieldPath(rowData.workbookVizportalUrlId, rowData.id) +
    filterToEmbeddedDatasource(rowData.embeddedDatasourceNodeId) :
    urls.datasourceLineageFieldPath(rowData.publishedDatasourceVizportalUrlId, rowData.id);
}

function filterToEmbeddedDatasource(datasourceId: string): string {
  return `?${CatalogUrlParamKeys.embeddedDataSource}=` + datasourceId;
}

function nameForLink(rowData: ColumnFieldsGridRowData): string | null {
  if (rowData.name === null) {
    return null;
  }

  return Messages.LinkNameWithParent({ name: rowData.name, parentName: rowData.datasourceName });
}

export type ColumnFieldsQueryGridProps = {
  readonly columnId: string;
  readonly rowCount: number | null;
};

export function ColumnFieldsQueryGrid(props: ColumnFieldsQueryGridProps): JSX.Element {
  const emptyGridMessage = Messages.EmptyColumnUsedByFieldsGridMessage();
  return (
    <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
      queryGql={ColumnFieldsQueryGql}
      partialQueryVariables={{ columnId: props.columnId }}
      selectConnectionListResult={selectConnectionListResult}
      mapResultNode={mapResultNode}
    >
      {({ rowStore }) =>
        <LinksDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          link={urlForLink}
          name={nameForLink}
        />
      }
    </PaginatedQueryWrapper>
  );
}
